import { errorMessage } from "@api/errors";
import { useUserMe } from "@api/generated/user/user";
import { currentHref } from "@app/legacyPaths";
import { useSettings } from "@app/workspace";
import { applyLanguage } from "@i18n";
import { Center, Loader, Stack, Text } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { AppShell } from "@shell/AppShell";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // an old (Hungarian) page path or search parameter goes to its English successor, keeping the rest and the hash
  beforeLoad: ({ location }) => {
    const to = currentHref(location.pathname, location.searchStr);
    if (to) throw redirect({ href: `${to}${location.hash ? `#${location.hash}` : ""}`, replace: true });
  },
  component: Root,
});

/** The notifications live inside the router: a notification may carry a link (the job of an export, the undo's log). */
function Root() {
  return (
    <>
      <Notifications position="bottom-right" />
      <RootLayout />
    </>
  );
}

/** Screens with their own full-screen frame (the phone field mode) skip the desktop shell. */
const BARE = ["/field"];

function RootLayout() {
  const { t } = useTranslation();
  const me = useUserMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const language = useSettings()?.language;
  // the UI language follows the account (Settings → Language), on every screen, the phone field mode included
  useEffect(() => {
    if (language) void applyLanguage(language);
  }, [language]);

  if (me.isPending) {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Loader />
          <Text c="dimmed">{t("user.signingIn")}</Text>
        </Stack>
      </Center>
    );
  }
  if (me.isError) {
    return (
      <Center h="100vh">
        <Text c="red">{errorMessage(me.error)}</Text>
      </Center>
    );
  }
  if (BARE.some((p) => pathname.startsWith(p))) return <Outlet />;
  return (
    <AppShell me={me.data}>
      <Outlet />
    </AppShell>
  );
}
